import React from 'react';
import PropTypes from 'prop-types';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableOptionRow from './SortableOptionRow';
import RemoveConfirmModal from './RemoveConfirmModal';
import { venueColorVars } from './MatrixCell';

const FIRST_COL_W = 480;
const CELL_W = 52;

const MatrixGrid = ({
  settings,
  venueOwnerships,
  draft,
  sensors,
  onDragEnd,
  onCycleCell,
  onRename,
  confirmRemoveId,
  setConfirmRemoveId,
  onRemoveConfirmed,
}) => {
  const gridTemplateColumns = `${FIRST_COL_W}px repeat(${venueOwnerships.length}, ${CELL_W}px)`;

  return (
    <div className="ar-matrix-wrap">
      <div className="ar-matrix">
        <div className="ar-matrix__header-row" style={{ gridTemplateColumns }}>
          <div className="ar-matrix__head-cell ar-matrix__head-cell--option">Option</div>
          {venueOwnerships.map((vo) => (
            <div key={vo.id} className="ar-matrix__head-cell" style={venueColorVars()}>
              <div className="ar-matrix__venue-name">{vo.name}</div>
            </div>
          ))}
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext
            items={settings.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {settings.map((setting) => (
              <SortableOptionRow
                key={setting.id}
                setting={setting}
                gridTemplateColumns={gridTemplateColumns}
                draft={draft}
                venueOwnerships={venueOwnerships}
                onRename={onRename}
                onRequestRemove={setConfirmRemoveId}
                onCycleCell={onCycleCell}
                isPendingRemoval={confirmRemoveId === setting.id}
              />
            ))}
          </SortableContext>
        </DndContext>

        <div className="ar-matrix__count-row" style={{ gridTemplateColumns }}>
          <div className="ar-matrix__count-label">Enabled count</div>
          {venueOwnerships.map((vo) => (
            <div key={vo.id} className="ar-matrix__count-cell">
              {(draft[vo.id]?.enabled.size) || 0}
            </div>
          ))}
        </div>
      </div>

      <div className="ar-legend">
        <span className="ar-legend__item">
          <span className="ar-legend__swatch--enabled">
            <i className="fas fa-check" />
          </span>
          Enabled
        </span>
        <span className="ar-legend__item">
          <span className="ar-legend__swatch--disabled" />
          Disabled
        </span>
        <span className="ar-legend__item">
          <span className="ar-legend__swatch--default">
            <i className="fas fa-star" />
          </span>
          Venue default
        </span>
      </div>

      <RemoveConfirmModal
        option={settings.find((s) => s.id === confirmRemoveId) || null}
        onCancel={() => setConfirmRemoveId(null)}
        onConfirm={() => {
          const id = confirmRemoveId;
          setConfirmRemoveId(null);
          if (id) onRemoveConfirmed(id);
        }}
      />
    </div>
  );
};

MatrixGrid.propTypes = {
  settings: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, name: PropTypes.string.isRequired })
  ).isRequired,
  venueOwnerships: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, name: PropTypes.string.isRequired })
  ).isRequired,
  draft: PropTypes.object.isRequired,
  sensors: PropTypes.array.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onCycleCell: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  confirmRemoveId: PropTypes.number,
  setConfirmRemoveId: PropTypes.func.isRequired,
  onRemoveConfirmed: PropTypes.func.isRequired,
};

MatrixGrid.defaultProps = {
  confirmRemoveId: null,
};

export default MatrixGrid;
